/*
 * Dealer locator for sections/kl-dealer-locator.liquid.
 * Every dealer is normalized once (clean country name, services, search text),
 * so the search box, country dropdown, service filters and "Near me" all work
 * on the same data and always agree. The current search is mirrored in the URL.
 */

(function () {
  if (customElements.get('kl-dealer-locator')) return;

  // Country names in the sheet are inconsistent; these map them to one display name.
  const COUNTRY_NAMES = {
    'u.s.a.': 'United States',
    usa: 'United States',
    'united states of america': 'United States',
    'u.k.': 'United Kingdom',
    uk: 'United Kingdom',
  };

  // Extra words that should find a country when typed in the search box.
  const COUNTRY_ALIASES = {
    'United States': 'usa us u.s.a. america',
    'United Kingdom': 'uk u.k. england scotland wales britain great britain',
    Netherlands: 'holland',
    'Czech Republic': 'czechia',
  };

  const regionNames = (() => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' });
    } catch (error) {
      return null;
    }
  })();

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  function isYes(value) {
    return /^(y|yes|x|true)$/i.test(String(value || '').trim());
  }

  function countryFor(record) {
    const merged = String(record['Merge Country Name'] || '').trim();
    if (merged) return COUNTRY_NAMES[merged.toLowerCase()] || merged;

    const code = String(record['Country Code'] || '').trim().toUpperCase();
    if (regionNames && /^[A-Z]{2}$/.test(code)) {
      const name = regionNames.of(code);
      if (name && name !== code) return name;
    }
    return 'Other';
  }

  function websiteFor(value) {
    const url = String(value || '').trim();
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }

  function normalizeDealer(record, index) {
    const country = countryFor(record);
    const code = String(record['Country Code'] || '').trim();
    const latitude = parseFloat(record.Latitude);
    const longitude = parseFloat(record.Longitude);

    const dealer = {
      id: index,
      name: String(record.Name || '').trim(),
      address: [record.Address, record['Address 2']].map((part) => String(part || '').trim()).filter(Boolean).join(', '),
      city: String(record.City || '').trim(),
      region: String(record['Province/State'] || '').trim(),
      postal: String(record['Postal/Zip Code'] || '').trim(),
      country,
      phone: String(record.Phone || record.Mobile || '').trim(),
      email: String(record.Email || '').trim(),
      website: websiteFor(record.Website),
      services: {
        repair: isYes(record['Repair Services (Y/N)']),
        rulon: isYes(record['RULON Boutique']) || /rulon/i.test(record.Brand || ''),
        custom: isYes(record['Custom Shop (Y/N)']),
      },
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null,
      distance: null,
    };

    // Leading space lets search terms match from the start of any word (" usa" won't match "musa").
    dealer.search = ' ' + normalizeText(
      [dealer.name, dealer.address, dealer.city, dealer.region, dealer.postal, country, code, COUNTRY_ALIASES[country]].join(' ')
    );
    return dealer;
  }

  function distanceKm(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km) {
    const miles = km * 0.621371;
    return km < 10 ? `${km.toFixed(1)} km · ${miles.toFixed(1)} mi` : `${Math.round(km)} km · ${Math.round(miles)} mi`;
  }

  class KlDealerLocator extends HTMLElement {
    async connectedCallback() {
      if (this.dealers || this.loading) return;
      this.loading = true;
      const controls = [...this.querySelectorAll('input, select, button')];
      controls.forEach((control) => { control.disabled = true; });
      const count = this.querySelector('[data-locator-count]');
      count.textContent = this.dataset.labelLoading;
      let records;
      try {
        records = await this.loadRecords();
      } catch (error) {
        count.textContent = this.dataset.labelError;
        this.loading = false;
        return;
      }
      controls.forEach((control) => { control.disabled = false; });
      this.loading = false;
      this.dealers = records.filter((record) => String(record.Name || '').trim()).map(normalizeDealer);

      this.form = this.querySelector('form');
      this.searchInput = this.querySelector('[data-locator-search]');
      this.countrySelect = this.querySelector('[data-locator-country]');
      this.serviceInputs = [...this.querySelectorAll('[data-locator-service]')];
      this.nearButton = this.querySelector('[data-locator-near]');
      this.results = this.querySelector('[data-locator-results]');
      this.count = this.querySelector('[data-locator-count]');
      this.notice = this.querySelector('[data-locator-notice]');
      this.clearButton = this.querySelector('[data-locator-clear]');
      this.emptyTemplate = this.querySelector('[data-locator-empty]');
      this.radius = parseFloat(this.dataset.radius) || 150;
      this.userLocation = null;
      this.postalCache = new Map();

      this.populateCountries();
      this.readUrl();
      this.bindEvents();
      this.update({ skipUrl: true });
    }

    async loadRecords() {
      let payload = this.ownerDocument.querySelector('[data-locator-data]');
      if (!payload) throw new Error('Missing dealer payload');
      // Always start at page one, including when a shared URL contains pagination.
      const param = payload.dataset.pageParam || 'page';
      if (Number(payload.dataset.currentPage) !== 1) {
        const first = new URL(this.dataset.pageUrl, window.location.origin);
        first.searchParams.set(param, '1');
        payload = await this.fetchPayload(first);
      }
      const records = [];
      const visited = new Set();
      const expected = Number(payload.dataset.total);
      while (payload) {
        const batch = JSON.parse(payload.textContent);
        if (!Array.isArray(batch)) throw new Error('Invalid dealer payload');
        records.push(...batch);
        const next = payload.dataset.next;
        if (!next) break;
        const url = new URL(this.dataset.pageUrl, window.location.origin);
        const nextUrl = new URL(next, url);
        const nextPage = nextUrl.searchParams.get(param) || nextUrl.searchParams.get('page');
        url.searchParams.set(param, nextPage);
        url.searchParams.set('page', nextPage);
        if (visited.has(url.href) || visited.size >= 100) throw new Error('Repeated dealer page');
        visited.add(url.href);
        payload = await this.fetchPayload(url);
      }
      if (Number.isFinite(expected) && records.length !== expected) throw new Error('Incomplete dealer list');
      return records;
    }

    async fetchPayload(url) {
      if (url.origin !== window.location.origin) throw new Error('Invalid dealer origin');
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = doc.querySelector('[data-locator-data]');
      if (!payload) throw new Error('Dealer data unavailable');
      return payload;
    }

    populateCountries() {
      const counts = {};
      this.dealers.forEach((dealer) => {
        counts[dealer.country] = (counts[dealer.country] || 0) + 1;
      });
      Object.keys(counts)
        .sort((a, b) => (a === 'Other') - (b === 'Other') || a.localeCompare(b))
        .forEach((country) => {
          const option = document.createElement('option');
          option.value = country;
          option.textContent = `${country} (${counts[country]})`;
          this.countrySelect.appendChild(option);
        });
    }

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('q')) this.searchInput.value = params.get('q');
      const country = params.get('country');
      if (country && [...this.countrySelect.options].some((option) => option.value === country)) {
        this.countrySelect.value = country;
      }
      const services = params.getAll('service');
      this.serviceInputs.forEach((input) => {
        input.checked = services.includes(input.value);
      });
    }

    writeUrl() {
      const url = new URL(window.location.href);
      ['q', 'country', 'service'].forEach((key) => url.searchParams.delete(key));
      const query = this.searchInput.value.trim();
      if (query) url.searchParams.set('q', query);
      if (this.countrySelect.value) url.searchParams.set('country', this.countrySelect.value);
      this.serviceInputs.filter((input) => input.checked).forEach((input) => url.searchParams.append('service', input.value));
      window.history.replaceState(window.history.state, '', url);
    }

    bindEvents() {
      let timer;
      this.searchInput.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.update(), 150);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.update();
      });
      this.countrySelect.addEventListener('change', () => this.update());
      this.serviceInputs.forEach((input) => input.addEventListener('change', () => this.update()));
      this.clearButton.addEventListener('click', () => this.clearAll());
      if (this.nearButton) this.nearButton.addEventListener('click', () => this.toggleNearMe());
    }

    clearAll() {
      this.searchInput.value = '';
      this.countrySelect.value = '';
      this.serviceInputs.forEach((input) => {
        input.checked = false;
      });
      this.setLocation(null);
      this.update();
    }

    setNearLabel(key) {
      if (!this.nearButton) return;
      this.nearButton.querySelector('span').textContent = this.nearButton.dataset[key];
      this.nearButton.classList.toggle('is-active', key === 'labelActive');
      this.nearButton.setAttribute('aria-pressed', key === 'labelActive' ? 'true' : 'false');
    }

    setLocation(location) {
      this.userLocation = location;
      this.setNearLabel(location ? 'labelActive' : 'label');
    }

    toggleNearMe() {
      if (this.userLocation) {
        this.setLocation(null);
        this.update();
        return;
      }
      if (!navigator.geolocation) {
        this.showNotice('Your browser does not support location. Try searching by city or country instead.');
        return;
      }
      this.setNearLabel('labelLoading');
      this.nearButton.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.nearButton.disabled = false;
          this.setLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
          this.update();
        },
        () => {
          this.nearButton.disabled = false;
          this.setLocation(null);
          this.showNotice('We could not get your location. Allow location access, or search by city or country instead.');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    }

    showNotice(message) {
      this.notice.textContent = message || '';
      this.notice.hidden = !message;
    }

    // A US ZIP (98101 or 98101-1234) or Canadian postal code (V6B 1A1 / V6B) typed in the search
    // box is looked up once (free Zippopotam service) and treated like "Near me" from that spot.
    postalQuery() {
      const raw = this.searchInput.value.trim().toUpperCase();
      const us = raw.match(/^(\d{5})(?:-\d{4})?$/);
      if (us) return { key: `us/${us[1]}`, label: us[1] };
      const ca = raw.match(/^([A-Z]\d[A-Z])\s?(?:\d[A-Z]\d)?$/);
      if (ca) return { key: `ca/${ca[1]}`, label: raw };
      return null;
    }

    lookupPostal(key) {
      if (this.postalCache.has(key)) return this.postalCache.get(key);
      this.postalCache.set(key, 'pending');
      fetch(`https://api.zippopotam.us/${key}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          const place = data && data.places && data.places[0];
          const latitude = place ? parseFloat(place.latitude) : NaN;
          const longitude = place ? parseFloat(place.longitude) : NaN;
          this.postalCache.set(key, Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : 'none');
        })
        .catch(() => this.postalCache.set(key, 'none'))
        .finally(() => {
          const current = this.postalQuery();
          if (current && current.key === key) this.update({ skipUrl: true });
        });
      return 'pending';
    }

    filter({ ignoreText = false } = {}) {
      const terms = ignoreText ? [] : normalizeText(this.searchInput.value).split(' ').filter(Boolean);
      const country = this.countrySelect.value;
      const services = this.serviceInputs.filter((input) => input.checked).map((input) => input.value);

      return this.dealers.filter(
        (dealer) =>
          (!country || dealer.country === country) &&
          services.every((service) => dealer.services[service]) &&
          terms.every((term) => dealer.search.includes(` ${term}`))
      );
    }

    update({ skipUrl = false } = {}) {
      const postal = this.postalQuery();
      const postalSpot = postal ? this.lookupPostal(postal.key) : null;
      const usePostal = postalSpot && typeof postalSpot === 'object';
      let matches = this.filter({ ignoreText: usePostal });
      let notice = '';
      if (postalSpot === 'pending' && !matches.length) notice = 'Finding dealers near that postal code…';

      const origin = usePostal ? postalSpot : this.userLocation;
      if (origin) {
        const { latitude, longitude } = origin;
        matches = matches
          .filter((dealer) => dealer.latitude !== null)
          .map((dealer) => ({ ...dealer, distance: distanceKm(latitude, longitude, dealer.latitude, dealer.longitude) }))
          .sort((a, b) => a.distance - b.distance);

        const nearby = matches.filter((dealer) => dealer.distance <= this.radius);
        if (nearby.length) {
          matches = nearby;
        } else if (matches.length) {
          matches = matches.slice(0, 6);
          notice = usePostal
            ? `No dealers within ${this.radius} km of ${postal.label}. Here are the closest ones.`
            : `No dealers within ${this.radius} km of you. Here are the closest ones.`;
        }
      }

      this.showNotice(notice);
      this.render(matches);

      const hasFilters =
        this.searchInput.value.trim() || this.countrySelect.value || this.serviceInputs.some((input) => input.checked) || this.userLocation;
      this.clearButton.hidden = !hasFilters;

      const label = this.dataset.labelResults || 'Showing [count] of [total] dealers';
      this.count.textContent = label.replace('[count]', matches.length).replace('[total]', this.dealers.length);

      if (!skipUrl) this.writeUrl();
    }

    render(dealers) {
      if (!dealers.length) {
        this.results.innerHTML = '';
        this.results.appendChild(this.emptyTemplate.content.cloneNode(true));
        return;
      }

      // Near me or a ZIP/postal search: one list by distance. Otherwise: grouped by country, then region, city and name.
      if (dealers.some((dealer) => dealer.distance !== null && dealer.distance !== undefined)) {
        this.results.innerHTML = `<div class="kl-locator__grid">${dealers.map((dealer) => this.card(dealer)).join('')}</div>`;
        return;
      }

      const groups = {};
      dealers.forEach((dealer) => {
        (groups[dealer.country] = groups[dealer.country] || []).push(dealer);
      });

      this.results.innerHTML = Object.keys(groups)
        .sort((a, b) => (a === 'Other') - (b === 'Other') || a.localeCompare(b))
        .map((country) => {
          const list = groups[country].sort(
            (a, b) => a.region.localeCompare(b.region) || a.city.localeCompare(b.city) || a.name.localeCompare(b.name)
          );
          return `
            <section class="kl-locator__group" aria-label="${escapeHtml(country)}">
              <h2 class="kl-locator__country">${escapeHtml(country)} <span>${list.length}</span></h2>
              <div class="kl-locator__grid">${list.map((dealer) => this.card(dealer)).join('')}</div>
            </section>`;
        })
        .join('');
    }

    card(dealer) {
      const place = [dealer.city, dealer.region, dealer.postal].filter(Boolean).join(', ');
      const fullAddress = [dealer.address, place, dealer.country].filter(Boolean).join(', ');
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([dealer.name, fullAddress].join(', '))}`;
      const tel = dealer.phone.replace(/[^\d+]/g, '');

      const badges = [
        dealer.services.rulon ? '<span class="kl-dealer__badge kl-dealer__badge--rulon">RULON boutique</span>' : '',
        dealer.services.custom ? '<span class="kl-dealer__badge">Custom shop</span>' : '',
        dealer.services.repair ? '<span class="kl-dealer__badge">Repair services</span>' : '',
      ].join('');

      return `
        <article class="kl-dealer">
          <h3 class="kl-dealer__name">${escapeHtml(dealer.name)}</h3>
          ${dealer.distance !== null ? `<p class="kl-dealer__distance">${formatDistance(dealer.distance)}</p>` : ''}
          <ul class="kl-dealer__details" role="list">
            ${dealer.address ? `<li><a href="${mapUrl}" target="_blank" rel="noopener">${escapeHtml(dealer.address)}${place ? `<br>${escapeHtml(place)}` : ''}</a></li>` : `<li>${escapeHtml([place, dealer.country].filter(Boolean).join(', '))}</li>`}
            ${dealer.phone ? `<li><a href="tel:${escapeHtml(tel)}">${escapeHtml(dealer.phone)}</a></li>` : ''}
            ${dealer.email ? `<li><a href="mailto:${escapeHtml(dealer.email)}">${escapeHtml(dealer.email)}</a></li>` : ''}
            ${dealer.website ? `<li><a href="${escapeHtml(dealer.website)}" target="_blank" rel="noopener">Visit website</a></li>` : ''}
          </ul>
          ${badges ? `<div class="kl-dealer__badges">${badges}</div>` : ''}
        </article>`;
    }
  }

  customElements.define('kl-dealer-locator', KlDealerLocator);
})();
